import { SaveLeadFormDraftParams } from '@plura/shared/types';
import { v4 as uuidv4 } from 'uuid';
import themes from './_themes';
import { generateCustomID } from '@plura/shared/utils';

// Export a function that generates fresh UUIDs each time it's called
const generatePostEventFeedbackSurveyTemplate = (): SaveLeadFormDraftParams => {
  const formId = uuidv4();
  const collectionId = generateCustomID(18)
  const workspaceId = uuidv4();

  // Generate new UUIDs for all blocks to ensure uniqueness
  const welcomeScreenId = uuidv4();
  const welcomeScreenBlockId = uuidv4();
  const emailId = uuidv4();
  const emailBlockId = uuidv4();
  const ratingId = uuidv4();
  const ratingBlockId = uuidv4();
  const multipleChoiceId = uuidv4();
  const multipleChoiceBlockId = uuidv4();
  const optionScaleId = uuidv4();
  const optionScaleBlockId = uuidv4();
  const longTextId = uuidv4();
  const longTextBlockId = uuidv4();
  const endScreenId = uuidv4();
  const endScreenBlockId = uuidv4();

  // Generate new UUIDs for select options - Multiple choice (what stood out)
  const mcOption1Id = uuidv4(); // Activities
  const mcOption2Id = uuidv4(); // Atmosphere
  const mcOption3Id = uuidv4(); // Guest hosts
  const mcOption4Id = uuidv4(); // Surprises & giveaways

  // Generate new UUIDs for media
  const welcomeMediaId = uuidv4();
  const ratingMediaId = uuidv4();
  const endScreenMediaId = uuidv4();

  return {
    ...themes.darkMidnight,
    "collectionId": collectionId,
    "name": "Post-Event Survey",
    "overview": "This is a form to collect leads",
    "about": "We're looking to connect with new leads",
    "tone": "Casual + Professional",
    "hideBuiltWithBadge": false,
    "closed": false,
    "closedMessage": "",
    "legalDisclaimer": false,
    "legalMessage": "",
    "notifications": false,
    "notificationEmail": "",
    "logoUrl": "https://s3.amazonaws.com/leadrr-prod-uploads/uploads/0d5b697e-e386-46ef-a2c7-34044737a773/GOLDLEAF-CO.png",
    "transitionType": "SLIDE_VERTICAL",
    "transitionSpeed": 500,
    "skipWelcomeScreen": false,
    "googleTagManager": "",
    "metaPixel": "",
    "assistantId": `asst_${collectionId}`,
    "fontSizeSetting": "LARGE",
    "customEndScreen": false,
    "hiddenFields": [],
    "journeyItems": [
      {
        "type": "WELCOME_SCREEN",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_FULL",
        "id": welcomeScreenId,
        "blockId": welcomeScreenBlockId,
        "content": "How did you like the event?",
        "manditory": false,
        "order": 0,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "Your thoughts help us level up future gatherings, this will only take a moment.",
        "thirdContent": "Give Feedback",
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": 48,
        "media": {
          "provider": "PEXEL",
          "id": welcomeMediaId,
          "type": "video",
          "name": "1692701",
          "link": "https://videos.pexels.com/video-files/1692701/1692701-hd_1280_720_30fps.mp4",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "thumbnail": "https://images.pexels.com/videos/1692701/pictures/preview-0.jpg"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      },
      {
        "type": "EMAIL",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": emailId,
        "blockId": emailBlockId,
        "content": "First things first, what's your email?",
        "manditory": true,
        "order": 1,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "We'll share any follow-up updates here.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      },
      {
        "type": "RATING",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "SPLIT_RIGHT",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": ratingId,
        "blockId": ratingBlockId,
        "content": "Overall, how would you rate the event?",
        "manditory": false,
        "order": 2,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": 5,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": null,
        "forthContent": null,
        "icon": "star",
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": 100,
        "media": {
          "provider": "UNSPLASH",
          "id": ratingMediaId,
          "type": "image",
          "name": "group of people raising there hands in concert",
          "link": "https://unsplash.com/photos/aWXVxy8BSzc/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHw3fHxmZXN0aXZhbHxlbnwwfHx8fDE3NjQ0OTM0NTh8MA",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "author": "Hanny Naibaho",
          "authorProfile": "https://unsplash.com/@hannynaibaho",
          "downloadLink": "https://api.unsplash.com/photos/aWXVxy8BSzc/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHw3fHxmZXN0aXZhbHxlbnwwfHx8fDE3NjQ0OTM0NTh8MA"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      },
      {
        "type": "MULTIPLE_CHOICE",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": multipleChoiceId,
        "blockId": multipleChoiceBlockId,
        "content": "What stood out to you the most?",
        "manditory": true,
        "order": 3,
        "selectOptions": [
          {
            "id": mcOption1Id,
            "order": 0,
            "value": "Activities"
          },
          {
            "id": mcOption2Id,
            "order": 1,
            "value": "Atmosphere"
          },
          {
            "id": mcOption3Id,
            "order": 2,
            "value": "Guest hosts"
          },
          {
            "id": mcOption4Id,
            "order": 3,
            "value": "Surprises & giveaways"
          }
        ],
        "multiSelect": true,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "Rank these from least enjoyed to most enjoyed.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      },
      {
        "type": "OPTION_SCALE",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": optionScaleId,
        "blockId": optionScaleBlockId,
        "content": "Would you attend another event like this in the future?",
        "manditory": true,
        "order": 4,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": 1,
        "maxRange": 10,
        "minLabel": "I don't think so",
        "maxLabel": "I'll be there!",
        "secondaryContent": null,
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      },
      {
        "type": "LONG_TEXT",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": longTextId,
        "blockId": longTextBlockId,
        "content": "Anything else you'd like to share with us?",
        "manditory": false,
        "order": 5,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "Your feedback helps shape what comes next.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      },
      {
        "type": "END_SCREEN",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_FULL",
        "id": endScreenId,
        "blockId": endScreenBlockId,
        "content": "That's everything, thanks for your time!",
        "manditory": false,
        "order": 6,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "We hope to see you again soon.",
        "thirdContent": "Join Next Year's Waiting List",
        "forthContent": "https://www.formsuite.co",
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": true,
        "scoreValues": null,
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": 37,
        "media": {
          "provider": "UNSPLASH",
          "id": endScreenMediaId,
          "type": "image",
          "name": "group of people standing on the field with bubbles",
          "link": "https://unsplash.com/photos/3BcNKoySAq0/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHw4fHxmZXN0aXZhbHxlbnwwfHx8fDE3NjQ0OTM0NTh8MA",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "author": "Samuel Regan-Asante",
          "authorProfile": "https://unsplash.com/@reganography",
          "downloadLink": "https://api.unsplash.com/photos/3BcNKoySAq0/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHw4fHxmZXN0aXZhbHxlbnwwfHx8fDE3NjQ0OTM0NTh8MA"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      }
    ],
    "snippets": [],
    "metaTitle": "Post-Event Survey",
    "gdprSetting": "NEVER",
    "gdprRequired": false,
    "consentMessageTitle": "I consent to the collection of my personal data"
  };
}

export default generatePostEventFeedbackSurveyTemplate;
